import { useState } from 'react';
import useTrialStore from '@/stores/TrialStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { cn } from '@/lib/utils';
import { trialSignUp } from '@/hooks/trial';

import { Button } from '../ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';

// Zod form to validate the user form
const formSchema = z
  .object({
    firstName: z
      .string({
        required_error: 'First name cannot be empty.',
      })
      .trim()
      .min(1, {
        message: 'First name cannot be empty.',
      }),
    lastName: z
      .string({
        required_error: 'Last name cannot be empty.',
      })
      .trim()
      .min(1, {
        message: 'Last name cannot be empty.',
      }),
    email: z
      .string({
        required_error: 'email cannot be empty.',
      })
      .trim()
      .email()
      .transform((val) => val.toLowerCase()),
    password: z
      .string()
      .min(8, {
        message: 'password must be at least 8 characters.',
      })
      .max(15, {
        message: 'password can be a max of 15 characters.',
      }),
    confirmPassword: z
      .string()
      .min(8, {
        message: 'password must be at least 8 characters.',
      })
      .max(15, {
        message: 'password can be a max of 15 characters.',
      }),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The passwords did not match',
        path: ['confirmPassword'],
      });
    }
  });

const TrialSignUpForm = ({ className, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const setTrialUserDetails = useTrialStore(
    (state) => state.setTrialUserDetails
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(values) {
    setIsLoading(true);

    try {
      const response = await trialSignUp(
        values.firstName,
        values.lastName,
        values.email,
        values.password
      );

      if (response.data.trial_id) {
        // Save trial details to store
        setTrialUserDetails({
          email: values.email,
          trial_id: response.data.trial_id,
          customer_id: response.data.customer_id,
          stripe_customer_id: response.data.stripe_customer_id,
          client_secret: response.data.client_secret,
          setup_intent_id: response.data.setup_intent_id,
        });

        // Navigate to payment page with trial_id and state
        navigate(`/onboarding/payment?trial_id=${response.data.trial_id}`, {
          state: {
            trial_id: response.data.trial_id,
          },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to create revio account'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-3'>
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='First Name'
                      type='text'
                      autoCorrect='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Last Name'
                      type='text'
                      autoCorrect='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Email'
                      type='email'
                      autoCorrect='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Password'
                      type='password'
                      autoCorrect='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Confirm Password'
                      type='password'
                      autoCorrect='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button disabled={isLoading} className='mt-2'>
              {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Continue
              {!isLoading && <ArrowRight className='ml-2 h-4 w-4' />}
            </Button>
          </div>
        </form>
      </Form>
      <p className='px-2 text-center text-xs text-muted-foreground'>
        By continuing, you agree to our{' '}
        <a
          href='https://www.getrevio.com/term-and-conditions'
          target='_blank'
          rel='noopener noreferrer'
          className='underline underline-offset-4 hover:text-primary'
        >
          Terms
        </a>{' '}
        and{' '}
        <a
          href='https://www.getrevio.com/term-and-conditions'
          target='_blank'
          rel='noopener noreferrer'
          className='underline underline-offset-4 hover:text-primary'
        >
          Privacy Policy
        </a>
      </p>
    </div>
  );
};

export default TrialSignUpForm;
